import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { imgUrl } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Building2, ImagePlus } from "lucide-react";

export default function EmpresaConfigModal({ open, onClose, empresa, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [lema, setLema] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPrev, setLogoPrev] = useState(null);
  const [portadaFile, setPortadaFile] = useState(null);
  const [portadaPrev, setPortadaPrev] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setNombre(empresa?.nombre || "");
      setLema(empresa?.lema || "");
      setLogoFile(null);
      setPortadaFile(null);
      setLogoPrev(empresa?.logo ? imgUrl(empresa.logo) : null);
      setPortadaPrev(empresa?.portada ? imgUrl(empresa.portada) : null);
    }
  }, [open, empresa]);

  const submit = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) return toast.error("El nombre de la empresa es obligatorio");
    setSaving(true);
    try {
      await api.patch("/empresa", { nombre, lema });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post("/empresa/logo", fd);
      }
      if (portadaFile) {
        const fd = new FormData();
        fd.append("file", portadaFile);
        await api.post("/empresa/portada", fd);
      }
      toast.success("Datos de la empresa actualizados");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="empresa-config-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-[#002B49]">Configuración de la empresa</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nombre de la empresa *</Label>
            <Input data-testid="empresa-nombre-input" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>Lema / frase (opcional)</Label>
            <Input data-testid="empresa-lema-input" value={lema} onChange={(e) => setLema(e.target.value)} placeholder="Ej. Construimos el futuro" />
          </div>
          <div className="grid grid-cols-3 gap-3 items-start">
            <label className="block cursor-pointer col-span-1">
              <Label className="mb-1.5 block">Logo</Label>
              <div className="relative h-24 rounded-lg overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center hover:border-[#00A3B5] transition-colors">
                {logoPrev ? (
                  <img src={logoPrev} alt="Logo" className="absolute inset-0 w-full h-full object-contain p-1" />
                ) : (
                  <Building2 size={22} className="text-slate-300" />
                )}
              </div>
              <input data-testid="empresa-logo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setLogoFile(f); setLogoPrev(URL.createObjectURL(f)); } }} />
            </label>
            <label className="block cursor-pointer col-span-2">
              <Label className="mb-1.5 block">Foto de portada general</Label>
              <div className="relative h-24 rounded-lg overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center hover:border-[#00A3B5] transition-colors">
                {portadaPrev ? (
                  <img src={portadaPrev} alt="Portada" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-slate-400">
                    <ImagePlus className="mx-auto" size={20} />
                    <p className="text-[10px] mt-1">Se muestra en la portada del sistema</p>
                  </div>
                )}
              </div>
              <input data-testid="empresa-portada-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPortadaFile(f); setPortadaPrev(URL.createObjectURL(f)); } }} />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button data-testid="empresa-guardar-button" type="submit" disabled={saving} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
